import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { isAdminSelector, organizationIdSelector, userIdSelector, userNamesSelector } from '@/domain/env';
import { createOrganization } from '@/domain/organization';
import { countries, dokkaCountries } from '@/lib/countries';

import AutocompleteBase from '@/components/mui/Form/Autocomplete/AutocompleteBase';
import helperTextError from '@/components/mui/Form/TextField/helperTextError';
import TextFieldRhf from '@/components/mui/Form/TextField/TextFieldRhf';

import { organizationFormSchema, type OrganizationFormValues } from '@/pages/common/Dialog/DialogCreateOrganization/schema';

const formID = 'create_org';
const options = countries.map((country) => ({ label: country, value: country }));

// The Dokka countries head the list, ruled off from the rest. The divider is
// drawn only while the input is empty, because that is the only time the list is
// unfiltered and the index still addresses the last of them. The popper is a
// *sibling* of the root solely because `disablePortal` keeps it out of a portal
// — remove that prop and the divider goes with it.
const CountryAutocomplete = styled(AutocompleteBase)(({ theme }) => ({
  [`&:has(.MuiAutocomplete-input[value=""]) + .MuiAutocomplete-popper .MuiAutocomplete-option[data-option-index="${
    dokkaCountries.length - 1
  }"]`]: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
}));

// `defaultValues` are seeded once, at mount, and `isValid` is derived from them
// — neither reset() nor async defaults recompute it. That is safe without a
// separate mount boundary here because every value they read lands in a single
// reducer case, before the route this dialog sits on is allowed to render.
const DialogCreateOrganization: React.FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const organizationId = useSelector(organizationIdSelector);
  const isAdmin = useSelector(isAdminSelector);
  const email = useSelector(userIdSelector);
  const { firstName, lastName } = useSelector(userNamesSelector);

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormSchema),
    mode: 'onTouched',
    defaultValues: {
      name: '',
      country: '',
      first_name: firstName ?? '',
      last_name: lastName ?? '',
      email: email ?? '',
    },
  });

  // Awaited so `isSubmitting` gates the button for the request's whole life —
  // the saga publishes no in-flight state of its own. The rejection is swallowed
  // because the saga has already toasted it and handleSubmit re-throws whatever
  // onSubmit rejects with.
  const onSubmit = (values: OrganizationFormValues) =>
    new Promise<void>((resolve, reject) => {
      dispatch(createOrganization({ ...values, resolve, reject }));
    }).catch(() => {});

  const isOpen = organizationId === null && isAdmin;

  return isOpen ? (
    <Dialog open={isOpen}>
      <DialogTitle sx={{ textAlign: 'center' }}>
        <FormattedMessage id="form.organization.create.title" defaultMessage="Organization Setup" />
      </DialogTitle>
      <DialogContent>
        {/* noValidate: validation is the schema's job. `required` stays for the
            asterisk, but MUI also puts it on the input, and the native bubble
            fires before the resolver and hides its message. */}
        <Grid container spacing={2} component="form" id={formID} noValidate onSubmit={handleSubmit(onSubmit)}>
          <Grid size={12} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" color="grey.600" sx={{ textAlign: 'center' }}>
              <FormattedMessage
                id="form.organization.create.subtitle"
                defaultMessage="Enter the basic information of your organization"
              />
            </Typography>
          </Grid>
          <Grid size={12}>
            <TextFieldRhf
              name="name"
              control={control}
              type="text"
              label={formatMessage({
                id: 'form.organization.create.name.label',
                defaultMessage: 'Organization name',
              })}
              placeholder={formatMessage({
                id: 'form.organization.create.name.placeholder',
                defaultMessage: 'Organization name',
              })}
              required
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <Controller
              name="country"
              control={control}
              render={({ field: { value, onChange, onBlur }, fieldState }) => (
                <CountryAutocomplete
                  value={value}
                  onChange={(event: React.SyntheticEvent, option: { value: string } | null) =>
                    onChange(option?.value ?? '')
                  }
                  onBlur={onBlur}
                  options={options}
                  disablePortal
                  autoHighlight
                  textFieldProps={{
                    label: formatMessage({
                      id: 'form.organization.create.country.placeholder',
                      defaultMessage: 'Select your country',
                    }),
                    required: true,
                    error: Boolean(fieldState.error),
                    // Always an element, so the helper line is reserved and the
                    // fields below do not shift when a message appears — the
                    // behaviour the four TextFields here already have.
                    helperText: helperTextError(fieldState.error?.message),
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={12} sx={{ mt: 2 }}>
            <Typography>
              <FormattedMessage id="form.organization.create.contactsTitle" defaultMessage="Contact Person information" />
            </Typography>
          </Grid>
          <Grid size={6}>
            <TextFieldRhf
              name="first_name"
              control={control}
              type="text"
              label={formatMessage({
                id: 'form.organization.create.firstName.label',
                defaultMessage: 'First name',
              })}
              placeholder={formatMessage({
                id: 'form.organization.create.firstName.placeholder',
                defaultMessage: 'Enter your first name',
              })}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 32 } }}
              required
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <TextFieldRhf
              name="last_name"
              control={control}
              type="text"
              label={formatMessage({
                id: 'form.organization.create.lastName.label',
                defaultMessage: 'Last name',
              })}
              placeholder={formatMessage({
                id: 'form.organization.create.lastName.placeholder',
                defaultMessage: 'Enter your last name',
              })}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 32 } }}
              required
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <TextFieldRhf
              name="email"
              control={control}
              type="email"
              label={formatMessage({
                id: 'form.organization.create.email.label',
                defaultMessage: 'Email',
              })}
              placeholder={formatMessage({
                id: 'form.organization.create.email.placeholder',
                defaultMessage: 'email@example.com',
              })}
              description={formatMessage({
                id: 'form.organization.create.email.hint',
                defaultMessage: 'Change it if you want to',
              })}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button form={formID} type="submit" disabled={isSubmitting || !isValid}>
          <FormattedMessage id="forms.common.continue" defaultMessage="Continue" />
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;
};

export default DialogCreateOrganization;
